import IconBlueLogo from '@listening/assets/astro/icons/icon-blue-logo.svg?react';
import ScatteredConnectors from '@listening/assets/astro/icons/new-design-25/compare/scattered-connectors.svg?react';
import {
  ArrowDown,
  Bookmark,
  Hourglass,
  Journal,
  LinkXmark,
  MailIn,
  Page,
  SoundHigh,
  WarningTriangle,
} from 'iconoir-react';
import type { ReactNode } from 'react';
import { SectionHeading } from './section-heading';
import * as m from '@/paraglide/messages';

function AppWindow({
  icon,
  kind,
  tag,
  className,
  children,
}: {
  icon: ReactNode;
  kind: string;
  tag?: string;
  className: string;
  children: ReactNode;
}) {
  return (
    <div
      className={`relative w-full max-w-[360px] rounded-2xl bg-white p-4 shadow-[0_1px_2px_rgb(0_0_0/0.05),0_12px_32px_-16px_rgb(0_0_0/0.18)] ring-1 ring-black/[0.06] ${className}`}
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-2.5'>
          <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-(--v2-card-bg) text-(--v2-chip-text)'>
            {icon}
          </span>
          <span className='truncate text-sm font-semibold text-(--v2-text-primary)'>
            {kind}
          </span>
        </div>
        {tag && (
          <span className='shrink-0 rounded-full bg-(--v2-card-bg) px-2.5 py-1 text-xs font-medium text-(--v2-chip-text)'>
            {tag}
          </span>
        )}
      </div>
      <div className='mt-4'>{children}</div>
    </div>
  );
}

function ReadAloud({ children }: { children: ReactNode }) {
  return (
    <span className='rounded bg-(--v2-badge-bg) px-1 font-medium text-(--v2-badge-text) ring-1 ring-(--v2-badge-text)/25'>
      {children}
    </span>
  );
}

function PdfCard() {
  return (
    <AppWindow
      className='z-10 lg:absolute lg:top-10 lg:left-0 lg:-rotate-1'
      icon={<Page className='size-4' />}
      kind='sleep-study.pdf'
      tag={m.why_listening_app_builtin()}
    >
      <p className='text-[13px] leading-6 text-(--v2-chip-text)'>
        Sleep loss narrows working memory <ReadAloud>[7, 12]</ReadAloud> and
        slows recall in young adults.<ReadAloud>³</ReadAloud>
      </p>
      <p className='mt-3 flex items-center gap-2 rounded-lg bg-(--v2-app-bg) px-2.5 py-2 text-xs text-(--v2-chip-text)'>
        <SoundHigh className='size-3.5 shrink-0 text-(--v2-badge-text)' />
        “…seven, twelve… footnote three”
      </p>
    </AppWindow>
  );
}

function ResearchReaderCard() {
  return (
    <AppWindow
      className='z-20 mt-3 lg:absolute lg:top-0 lg:right-6 lg:mt-0 lg:rotate-1'
      icon={<Journal className='size-4' />}
      kind={m.why_listening_app_research()}
      tag={m.why_listening_app_subscription()}
    >
      <div className='flex items-center justify-between text-xs'>
        <span className='font-medium text-(--v2-text-primary)'>
          {m.why_listening_listening_this_week()}
        </span>
        <span className='text-(--v2-chip-text)'>
          {m.why_listening_papers_only()}
        </span>
      </div>
      <div className='mt-2 h-2 overflow-hidden rounded-full bg-(--v2-card-bg)'>
        <div className='h-full w-[88%] rounded-full bg-(--v2-badge-text)' />
      </div>
      <p className='mt-3 flex items-center gap-1.5 text-xs font-medium text-(--v2-badge-text)'>
        <Hourglass className='size-3.5' />
        {m.why_listening_weekly_hours_used()}
      </p>
    </AppWindow>
  );
}

function ReadLaterCard() {
  return (
    <AppWindow
      className='z-30 mt-3 lg:absolute lg:bottom-0 lg:left-28 lg:mt-0 lg:rotate-[0.5deg]'
      icon={<Bookmark className='size-4' />}
      kind={m.why_listening_app_read_later()}
      tag={m.why_listening_app_subscription()}
    >
      <ul className='space-y-1.5 text-[13px]'>
        <li className='flex items-center gap-2 rounded-lg bg-(--v2-app-bg) px-2.5 py-2 text-(--v2-text-primary)'>
          <MailIn className='size-3.5 shrink-0 text-(--v2-chip-text)' />
          {m.why_listening_friday_newsletter()}
        </li>
        <li className='flex items-center gap-2 rounded-lg bg-(--v2-app-bg) px-2.5 py-2 text-(--v2-text-primary)'>
          <Bookmark className='size-3.5 shrink-0 text-(--v2-chip-text)' />
          {m.why_listening_saved_article()}
        </li>
        <li className='flex items-center justify-between gap-2 rounded-lg border border-dashed border-(--v2-card-border) px-2.5 py-2 text-(--v2-chip-text)'>
          <span className='flex items-center gap-2'>
            <Page className='size-3.5 shrink-0' />
            methods.pdf
          </span>
          <span className='text-[11px]'>
            {m.why_listening_pdfs_afterthought()}
          </span>
        </li>
      </ul>
    </AppWindow>
  );
}

function MainstreamReaderCard() {
  return (
    <AppWindow
      className='z-40 mt-3 lg:absolute lg:right-0 lg:bottom-12 lg:mt-0 lg:-rotate-1'
      icon={<SoundHigh className='size-4' />}
      kind={m.why_listening_app_mainstream()}
      tag={m.why_listening_app_subscription()}
    >
      <div className='flex gap-3 rounded-xl bg-(--v2-badge-bg) p-3'>
        <WarningTriangle className='mt-0.5 size-4 shrink-0 text-(--v2-badge-text)' />
        <div>
          <p className='text-[13px] font-semibold text-(--v2-text-primary)'>
            {m.why_listening_word_quota_used()}
          </p>
          <p className='mt-0.5 text-xs leading-5 text-(--v2-chip-text)'>
            {m.why_listening_upgrade_tier()}
          </p>
        </div>
      </div>
    </AppWindow>
  );
}

function BrokenLink({ className }: { className: string }) {
  return (
    <span
      className={`absolute hidden size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-(--v2-card-border) bg-(--v2-preview-bg) text-(--v2-text-muted) lg:flex ${className}`}
    >
      <LinkXmark className='size-4' />
    </span>
  );
}

function SubscriptionHub() {
  return (
    <div className='z-50 mt-8 flex items-center gap-3 rounded-full bg-(--v2-text-primary) py-2 pr-5 pl-2 text-white shadow-lg lg:absolute lg:top-1/2 lg:left-1/2 lg:mt-0 lg:-translate-x-1/2 lg:-translate-y-1/2'>
      <span className='flex size-8 items-center justify-center rounded-full bg-white/15 text-sm font-semibold'>
        3
      </span>
      <span className='text-sm font-medium whitespace-nowrap'>
        {m.why_listening_subscriptions_hub()}
      </span>
    </div>
  );
}

function ScatteredApps() {
  return (
    <div
      aria-hidden='true'
      className='relative mx-auto flex max-w-[1120px] flex-col items-center px-2 lg:block lg:h-[560px] lg:px-0'
    >
      <div className='absolute -inset-x-16 -inset-y-10 hidden bg-[radial-gradient(var(--v2-card-border)_1.2px,transparent_1.2px)] mask-radial-from-30% mask-radial-to-70% bg-size-[22px_22px] lg:block' />
      <ScatteredConnectors
        aria-hidden='true'
        className='absolute inset-0 hidden size-full text-(--v2-card-border) lg:block'
      />
      <BrokenLink className='top-[40%] left-[38%]' />
      <BrokenLink className='top-[35%] left-[61.7%]' />
      <BrokenLink className='top-[58.7%] left-[43.6%]' />
      <BrokenLink className='top-[60.7%] left-[61.2%]' />
      <PdfCard />
      <ResearchReaderCard />
      <ReadLaterCard />
      <MainstreamReaderCard />
      <SubscriptionHub />
    </div>
  );
}

function CompactAppRow({
  icon,
  name,
  limit,
}: {
  icon: ReactNode;
  name: string;
  limit: string;
}) {
  return (
    <li className='flex items-center gap-3 px-3 py-3'>
      <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-(--v2-card-bg) text-(--v2-chip-text)'>
        {icon}
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-sm font-semibold text-(--v2-text-primary)'>
          {name}
        </span>
        <span className='block truncate text-xs text-(--v2-badge-text)'>
          {limit}
        </span>
      </span>
      <span className='shrink-0 rounded-full bg-(--v2-card-bg) px-2.5 py-1 text-[11px] font-medium text-(--v2-chip-text)'>
        {m.why_listening_app_subscription()}
      </span>
    </li>
  );
}

function CompactApps() {
  return (
    <div aria-hidden='true' className='mx-auto mt-10 max-w-md lg:hidden'>
      <div className='rounded-3xl bg-white p-2 ring-1 ring-black/[0.06]'>
        <p className='flex items-center gap-2.5 rounded-2xl bg-(--v2-text-primary) py-2 pr-4 pl-2 text-sm font-medium text-white'>
          <span className='flex size-7 items-center justify-center rounded-full bg-white/15 text-xs font-semibold'>
            3
          </span>
          {m.why_listening_subscriptions_hub()}
        </p>
        <ul className='divide-y divide-(--v2-card-border)'>
          <CompactAppRow
            icon={<Journal className='size-4' />}
            limit={m.why_listening_weekly_hours_cap()}
            name={m.why_listening_app_research()}
          />
          <CompactAppRow
            icon={<Bookmark className='size-4' />}
            limit={m.why_listening_pdfs_afterthought()}
            name={m.why_listening_app_read_later()}
          />
          <CompactAppRow
            icon={<SoundHigh className='size-4' />}
            limit={m.why_listening_word_quota_used()}
            name={m.why_listening_app_mainstream()}
          />
        </ul>
      </div>
      <span className='mx-auto mt-3 flex size-9 items-center justify-center rounded-full bg-(--v2-preview-bg) text-(--v2-chip-text) ring-1 ring-(--v2-card-border)'>
        <ArrowDown className='size-4' />
      </span>
    </div>
  );
}

export function ScatteredAppsSection() {
  return (
    <section
      aria-labelledby='why-scattered-heading'
      className='overflow-x-clip px-5 py-14 sm:px-6 sm:py-24 lg:py-32'
    >
      <SectionHeading
        description={m.why_listening_scattered_description()}
        eyebrow={m.why_listening_scattered_eyebrow()}
        id='why-scattered-heading'
        title={m.why_listening_scattered_title()}
      />
      <CompactApps />
      <div className='hidden lg:mt-20 lg:block'>
        <ScatteredApps />
      </div>
      <div className='mt-3 flex justify-center lg:mt-16'>
        <p className='flex w-full max-w-md items-center gap-4 rounded-2xl bg-(--v2-app-bg) py-3 pr-5 pl-3 text-left ring-1 ring-black/[0.06] sm:w-auto sm:max-w-xl sm:pr-6'>
          <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-azure-595/10'>
            <IconBlueLogo aria-hidden='true' className='size-6' />
          </span>
          <span className='text-[15px] leading-snug text-(--v2-chip-text) sm:text-base'>
            <span className='font-semibold text-(--v2-text-primary)'>
              {m.why_listening_combine_title()}
            </span>{' '}
            {m.why_listening_combine_body()}
          </span>
        </p>
      </div>
    </section>
  );
}
